// src/index.js
import { fetchConfig } from './config-client.js';
import { createPanel }  from './panel.js';
import { mountIframe }  from './iframe.js';
import { exposeWindowAPI } from './window-api.js';

const EMBED_BASE = 'https://book.attraxion.app/embed';

// Hidden Apple Pay / Google Pay trial: ?applepay=1 on the venue's page rides along to
// the frame, which cannot see this page's URL. Remove once Dojo wallets go live.
export function withWalletTest(src, search) {
  try {
    return new URLSearchParams(search).get('applepay') === '1' ? `${src}${src.indexOf('?') === -1 ? '?' : '&'}applepay=1` : src;
  } catch { return src; }
}

const flowPanels = new Map(); // "venue:checkout" -> { panel, iframe, config }

function panelKey(venue, checkout) {
  return checkout ? `${venue}:${checkout}` : venue;
}

async function bootVenue(venue, checkout) {
  const key = panelKey(venue, checkout);
  if (flowPanels.has(key)) return flowPanels.get(key);
  const config = await fetchConfig({ venue, checkout });
  if (!config) return null; // silent bail

  const panel = createPanel({});
  const iframe = mountIframe(panel.panelEl, {
    src: withWalletTest(`${EMBED_BASE}/${encodeURIComponent(venue)}`, window.location.search),
    token: config.token,
    config,
  });

  // Iframe → loader messages (close, lock, bail)
  window.addEventListener('message', (e) => {
    if (!e.data || typeof e.data !== 'object') return;
    if (e.data.type === 'attraxion:close') panel.close();
    if (e.data.type === 'attraxion:lock')  panel.setLocked(!!e.data.locked);
    if (e.data.type === 'attraxion:bail')  panel.close();
  });

  const entry = { panel, iframe, config };
  flowPanels.set(key, entry);
  return entry;
}

function renderDefaultTrigger(host, config) {
  const btn = document.createElement('button');
  btn.className = 'attraxion-trigger';
  btn.setAttribute('data-attraxion-booking', 'default-trigger');
  btn.style.setProperty('--attraxion-color', config.theme?.color || '#6366f1');
  btn.textContent = config.theme?.button_label || 'Book Now';
  host.appendChild(btn);
  return btn;
}

class AttraxionBookingElement extends HTMLElement {
  async connectedCallback() {
    const venue = this.getAttribute('venue');
    const checkout = this.getAttribute('checkout');
    if (!venue) return;
    const entry = await bootVenue(venue, checkout);
    if (!entry) return; // silent bail
    const existingChild = this.firstElementChild;
    const trigger = existingChild || renderDefaultTrigger(this, entry.config);
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      entry.panel.open();
    });
  }
}

customElements.define('attraxion-booking', AttraxionBookingElement);

exposeWindowAPI({ bootVenue, flowPanels });
